import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { Txn, Group, Category, Budget, RecurringState } from '../money.model';
import { DonutSegment } from '../charts/donut-chart.component';
import { BarDatum } from '../charts/bar-chart.component';
import {
  spendableTxns, monthKey, recentMonths, monthlyTotals, groupMix, monthKpis, MonthKpis,
  categoryMix, UNCATEGORIZED_COLOR,
} from './dashboard.aggregate';
import { formatCents, formatDeltaLabel } from '../format';
import { budgetRows, BudgetRow } from '../budgets/budgets.calc';
import { detectRecurring, RecurringSeries } from '../rules/recurring.detector';
import { monthlyNut, visibleSeries, stateForLedger, allRecurringSeries } from '../recurring/recurring.calc';

/** A selection only means something within the month it was made in. */
export function selectionForMonth(selectedId: string | null, mix: DonutSegment[]): string | null {
  if (!selectedId) return null;
  return mix.some(s => s.id === selectedId) ? selectedId : null;
}

const TREND_MONTHS = 6;

/**
 * The rows the panel shows: most-consumed first, but every over-budget row
 * survives the cut — an overspend is the whole reason the panel exists.
 */
export function topBudgetRows(rows: BudgetRow[], limit: number): BudgetRow[] {
  // Sorted here rather than trusted from the caller: budgetRows() already
  // sorts by pctUsed, but a pure function should not depend on that.
  const sorted = [...rows].sort((a, b) => b.pctUsed - a.pctUsed);
  const over = sorted.filter(r => r.isOver);
  const rest = sorted.filter(r => !r.isOver);
  return [...over, ...rest].slice(0, Math.max(limit, over.length));
}

/**
 * Maps a trend-bar index back to the month key it represents, using the same
 * six-month window the trend was built from. Returns null when that window
 * month has no data — a window month with no data is still rendered as a zero
 * bar, but there is nothing to navigate to.
 */
export function trendMonthAt(
  index: number, anchorMonth: string, monthsWithData: string[],
): string | null {
  const window = recentMonths(anchorMonth, TREND_MONTHS);
  const month = window[index];
  if (month === undefined) return null;
  return monthsWithData.includes(month) ? month : null;
}

@Component({
  selector: 'app-money-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';

  months: string[] = [];
  month = '';

  kpis: MonthKpis = {
    spentCents: 0, incomeCents: 0, netCents: 0,
    txnCount: 0, prevSpentCents: 0, deltaPct: null,
  };
  mix: DonutSegment[] = [];
  trend: BarDatum[] = [];
  trendDisplay: string[] = [];
  trendAverage: number | null = null;
  /** The group whose categories the donut is showing, or null for the group view. */
  drilledGroupId: string | null = null;
  drillMix: DonutSegment[] = [];
  budgets: BudgetRow[] = [];
  recurring: RecurringSeries[] = [];
  nutCents = 0;

  groups: Group[] = [];

  categories: Category[] = [];

  private all: Txn[] = [];
  private budgetsByCategory: Record<string, Budget> = {};
  private recurringStateByHash: Record<string, RecurringState> = {};
  private subs: Subscription[] = [];

  readonly fmt = formatCents;

  constructor(
    private afAuth: AngularFireAuth,
    private money: MoneyService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      this.subs.push(this.money.subscribeAll(user.uid).subscribe({
        next: data => {
          this.all = data.txns;
          this.groups = data.groups;
          this.categories = data.categories;
          this.budgetsByCategory = data.budgets;
          this.recurringStateByHash = data.recurringState;

          this.months = [...new Set(spendableTxns(data.txns).map(t => monthKey(t.date)))]
            .sort().reverse();
          if (!this.month || !this.months.includes(this.month)) {
            this.month = this.months[0] ?? '';
          }
          this.recompute();
          this.loading = false;
        },
        error: (e: unknown) => {
          this.error = `Could not load your data: ${(e as Error).message}. Try reloading the page.`;
          this.loading = false;
        },
      }));
    }));
  }

  // BOTH subscriptions: an unstored authState subscription outlives the
  // component and re-runs against a destroyed instance on the next auth event.
  ngOnDestroy(): void { this.subs.forEach(s => s.unsubscribe()); }

  /** Rows in the month currently shown. */
  private inMonthTxns(): Txn[] {
    return this.all.filter(t => monthKey(t.date) === this.month);
  }

  /**
   * Rebuild only the drilled slice. Separate from recompute() because a click
   * changes nothing the KPIs, trend, budgets or recurring panels depend on —
   * and because recompute() re-validates the drill against the month's mix,
   * which is right after a month change or a background write but wrong
   * immediately after a click that just set it.
   */
  private recomputeDrill(): void {
    if (!this.drilledGroupId) { this.drillMix = []; return; }
    const group = this.groups.find(g => g.id === this.drilledGroupId);
    this.drillMix = categoryMix(this.inMonthTxns(), this.categories, this.drilledGroupId,
                                 group?.color ?? UNCATEGORIZED_COLOR);
  }

  private recompute(): void {
    if (!this.month) {
      this.kpis = { spentCents: 0, incomeCents: 0, netCents: 0,
                    txnCount: 0, prevSpentCents: 0, deltaPct: null };
      this.mix = []; this.trend = []; this.trendDisplay = []; this.trendAverage = null;
      this.drilledGroupId = null;
      this.drillMix = [];
      return;
    }

    this.kpis = monthKpis(this.all, this.month);

    this.mix = groupMix(this.inMonthTxns(), this.categories, this.groups);
    // recompute() runs on every subscribeAll emission, not only on month
    // change, so a background write must not clobber an in-progress drill. A
    // drill survives as long as its group is still present in the mix; it is
    // cleared only when it is not.
    this.drilledGroupId = selectionForMonth(this.drilledGroupId, this.mix);
    this.recomputeDrill();

    const window = recentMonths(this.month, TREND_MONTHS);
    const totals = monthlyTotals(this.all, window);
    this.trend = totals.map(m => ({ label: this.monthShort(m.month), value: m.spentCents }));
    this.trendDisplay = totals.map(m => formatCents(m.spentCents));

    const withSpend = totals.filter(m => m.spentCents > 0);
    this.trendAverage = withSpend.length
      ? withSpend.reduce((n, m) => n + m.spentCents, 0) / withSpend.length
      : null;

    this.budgets = topBudgetRows(
      budgetRows(this.all, this.month, this.categories, this.budgetsByCategory), 4);

    // Deliberately NOT filtered to this.month: recurring describes what
    // recurs now, not what recurred in whatever month is being paged
    // through. Scoping it to this.month would make the Fixed/mo tile show
    // 2026's fixed costs beside a September-2024 heading. So this stays
    // computed from the whole ledger (this.all) and is labelled "as of
    // today" in the template instead of being month-scoped — do not
    // "fix" this into filtering by this.month.
    const asOf = new Date().toISOString().slice(0, 10);
    const detected = detectRecurring(this.all, asOf);
    // stateForLedger re-keys recurring-state (hashed for RTDB safety) back to
    // raw series keys, probing every possible billing-day split too. This
    // mirrors recurring.component.ts exactly so the two screens can never
    // again disagree about the fixed-cost total.
    const state = stateForLedger(this.all, detected, this.recurringStateByHash);
    const all = allRecurringSeries(this.all, detected, state, asOf);
    this.recurring = visibleSeries(all, state)
      .filter(s => s.direction === 'expense')
      .slice(0, 4);
    this.nutCents = monthlyNut(all, state);
  }

  /** ISO month to a short label. The 'T00:00:00' suffix keeps it in local time. */
  monthShort(m: string): string {
    return new Date(`${m}-01T00:00:00`)
      .toLocaleDateString('en-US', { month: 'short' });
  }

  get monthLabel(): string {
    return this.month
      ? new Date(`${this.month}-01T00:00:00`)
          .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : '';
  }

  get trendHighlightIndex(): number { return TREND_MONTHS - 1; }

  get averageLabel(): string {
    return this.trendAverage === null ? '' : `avg ${formatCents(this.trendAverage)}`;
  }

  get deltaLabel(): string { return formatDeltaLabel(this.kpis.deltaPct); }

  get uncategorizedCount(): number {
    return spendableTxns(this.all)
      .filter(t => t.amount < 0 && monthKey(t.date) === this.month && !t.categoryId).length;
  }

  get canGoBack(): boolean { return this.monthIndex < this.months.length - 1; }
  get canGoForward(): boolean { return this.monthIndex > 0; }
  private get monthIndex(): number { return this.months.indexOf(this.month); }

  // months is newest-first, so "previous" is the next index along.
  prevMonth(): void {
    if (this.canGoBack) { this.month = this.months[this.monthIndex + 1]; this.recompute(); }
  }

  nextMonth(): void {
    if (this.canGoForward) { this.month = this.months[this.monthIndex - 1]; this.recompute(); }
  }

  // The trend shows the six months ending at the current one; map the bar
  // index back to that month and navigate there, same as prevMonth/nextMonth.
  // A window month with no data still renders as a zero bar but is not
  // navigable — trendMonthAt returns null for it and we ignore the click.
  onSelectTrendMonth(index: number): void {
    const target = trendMonthAt(index, this.month, this.months);
    if (target === null) return;
    this.month = target;
    this.recompute();
  }

  trackSegment(_: number, s: DonutSegment): string { return s.id; }

  /** Group arcs, or the drilled group's category arcs. */
  get displayedMix(): DonutSegment[] {
    return this.drilledGroupId ? this.drillMix : this.mix;
  }

  get mixCenterLabel(): string {
    if (!this.drilledGroupId) return 'total';
    return this.groups.find(g => g.id === this.drilledGroupId)?.name ?? 'Group';
  }

  get mixCenterValue(): string {
    return formatCents(this.displayedMix.reduce((sum, s) => sum + s.value, 0));
  }

  /**
   * One entry point for both the donut and the legend, because they show the
   * same segments and must behave identically.
   *
   * Group level: drill in — except Uncategorized, which is a synthetic group
   * with no categories behind it, so the only useful action is opening those
   * rows to be categorized.
   * Category level: open the transactions that make up the slice.
   */
  onMixSelect(id: string): void {
    if (this.drilledGroupId) { this.openCategory(id); return; }
    if (id === 'uncategorized') { this.openUncategorized(); return; }
    this.drilledGroupId = id;
    this.recomputeDrill();
  }

  openCategory(categoryId: string): void {
    // The donut slice is spending only (spendableTxns then amount < 0), so
    // the list behind it must be too — spending: 1 seeds TxnFilters.spendingOnly.
    // Do NOT add this to openUncategorized below: that queue is for
    // categorizing, where every row regardless of sign is the point.
    this.router.navigate(['/money-manager/transactions'],
      { queryParams: { month: this.month, categoryId, spending: 1 } });
  }

  openUncategorized(): void {
    this.router.navigate(['/money-manager/transactions'],
      { queryParams: { month: this.month, uncategorized: 1 } });
  }

  backToGroups(): void {
    this.drilledGroupId = null;
    this.drillMix = [];
  }

  trackBudget(_: number, b: BudgetRow): string { return b.categoryId; }

  trackRecurring(_: number, s: RecurringSeries): string { return s.key; }
}
